import { LAKH } from "./types.ts";

/**
 * Cities are not just a cheapness multiplier.
 *
 * A tier-2 city and a hill station can share a cost index and still produce
 * very different plans, because what a remote town lacks is not affordability
 * but *access*: tertiary hospitals, international schools, an airport. Those
 * show up as real recurring rupees — trips to a metro for a specialist, a
 * boarding school instead of a day school — which a single index hides.
 *
 * So each city carries a profile, and the model reads more than one field.
 */
export type CityTier = "metro" | "tier2" | "small-town" | "hill-station";
export type Region = "North" | "West" | "South" | "East" | "Hill stations";

/** What the best locally available schooling looks like. */
export type SchoolingAccess =
  | "international" // IB / Cambridge day schools exist locally
  | "national" // good CBSE/ICSE day schools, no international tier
  | "boarding-hub" // famous residential schools, thin day-school options
  | "limited"; // local-board schools only

export interface CityProfile {
  id: string;
  name: string;
  region: Region;
  tier: CityTier;
  /**
   * Cost of the locally-priced basket, Bengaluru = 1.00.
   * Expatistan comparisons where available (Sept 2026), estimated otherwise —
   * `measured` says which. Hill-station figures already net off the two
   * opposing effects: housing and labour are much cheaper, while goods that
   * have to be trucked up are dearer.
   */
  index: number;
  measured: boolean;
  /**
   * Household cost, per year in today's rupees, of reaching services the town
   * does not have: specialist appointments in the nearest metro, the travel and
   * overnight stays around them, and the premium on getting to an airport.
   * Zero in a metro, where you drive across town instead.
   */
  remoteAccessPerYear: number;
  schooling: SchoolingAccess;
  /** Nearest super-specialty hospital, for the access figure to be judged against. */
  tertiaryCare: string;
  note: string;
}

export const CITIES: CityProfile[] = [
  // ---------------------------------------------------------------- North
  { id: "gurugram", name: "Gurugram", region: "North", tier: "metro", index: 1.03, measured: true,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "Marginally dearer than Bengaluru. Deepest private-healthcare market in the north; winter air quality is the standing cost nobody prices." },
  { id: "delhi", name: "Delhi", region: "North", tier: "metro", index: 0.95, measured: false,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "AIIMS and the largest concentration of specialists in India. Air quality is the reason many retirees leave anyway." },
  { id: "noida", name: "Noida / Greater Noida", region: "North", tier: "metro", index: 0.85, measured: false,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "NCR access at a discount to Gurugram, with the same winter air." },
  { id: "chandigarh", name: "Chandigarh / Mohali", region: "North", tier: "tier2", index: 0.68, measured: true,
    remoteAccessPerYear: 12_000, schooling: "national", tertiaryCare: "PGIMER, in the city",
    note: "32% cheaper than Bengaluru, and PGIMER is among the best public hospitals in the country. The strongest all-round northern option." },
  { id: "jaipur", name: "Jaipur", region: "North", tier: "tier2", index: 0.65, measured: false,
    remoteAccessPerYear: 15_000, schooling: "national", tertiaryCare: "In the city",
    note: "Good private hospitals, an international airport, and dry heat that suits some people and not others." },
  { id: "lucknow", name: "Lucknow", region: "North", tier: "tier2", index: 0.60, measured: false,
    remoteAccessPerYear: 20_000, schooling: "national", tertiaryCare: "SGPGI, in the city",
    note: "Among the cheapest cities with a genuine super-specialty hospital." },
  { id: "amritsar", name: "Amritsar / Ludhiana", region: "North", tier: "tier2", index: 0.62, measured: false,
    remoteAccessPerYear: 25_000, schooling: "national", tertiaryCare: "CMC Ludhiana",
    note: "Cheap, well connected by road, thinner on specialist depth than Chandigarh." },

  // ---------------------------------------------------------------- West
  { id: "mumbai", name: "Mumbai", region: "West", tier: "metro", index: 1.29, measured: true,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "29% dearer than Bengaluru, and housing alone is 71% dearer. The most expensive way to do this." },
  { id: "pune", name: "Pune", region: "West", tier: "metro", index: 0.78, measured: true,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "22% cheaper than Bengaluru with comparable schools and hospitals. The usual answer when someone wants metro amenities without metro prices." },
  { id: "ahmedabad", name: "Ahmedabad", region: "West", tier: "tier2", index: 0.72, measured: false,
    remoteAccessPerYear: 10_000, schooling: "national", tertiaryCare: "In the city",
    note: "Cheap, well run, strong hospitals. Note Gujarat's alcohol prohibition if that matters to your household." },
  { id: "goa", name: "Goa", region: "West", tier: "small-town", index: 0.85, measured: false,
    remoteAccessPerYear: 45_000, schooling: "national", tertiaryCare: "Manipal Goa; complex cases go to Mumbai",
    note: "Tourist pricing on everything tradeable offsets cheap housing. Serious medical cases still fly to Mumbai." },

  // ---------------------------------------------------------------- South
  { id: "bengaluru", name: "Bengaluru", region: "South", tier: "metro", index: 1.0, measured: true,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "The baseline for every other index here." },
  { id: "chennai", name: "Chennai", region: "South", tier: "metro", index: 0.82, measured: false,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "Arguably the best medical city in India, which matters more than the cost gap once you are past 70." },
  { id: "hyderabad", name: "Hyderabad", region: "South", tier: "metro", index: 0.80, measured: false,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "Metro amenities, cheaper housing than Bengaluru, good international schools." },
  { id: "kochi", name: "Kochi", region: "South", tier: "tier2", index: 0.72, measured: false,
    remoteAccessPerYear: 8_000, schooling: "international", tertiaryCare: "In the city",
    note: "Kerala's health indicators are the best in India, and the airport connects directly to the Gulf." },
  { id: "coimbatore", name: "Coimbatore", region: "South", tier: "tier2", index: 0.62, measured: false,
    remoteAccessPerYear: 10_000, schooling: "national", tertiaryCare: "Ganga and KG hospitals, in the city",
    note: "Repeatedly ranked the top retirement city in India: cheap, clean air, and genuinely good hospitals." },
  { id: "mysuru", name: "Mysuru", region: "South", tier: "tier2", index: 0.66, measured: true,
    remoteAccessPerYear: 18_000, schooling: "national", tertiaryCare: "In the city; complex cases go to Bengaluru",
    note: "About a third cheaper than Bengaluru and three hours from it, which is the real reason it works." },
  { id: "pondicherry", name: "Puducherry", region: "South", tier: "small-town", index: 0.68, measured: false,
    remoteAccessPerYear: 20_000, schooling: "national", tertiaryCare: "JIPMER, in the city",
    note: "JIPMER makes this the only small town on this list with world-class public tertiary care." },

  // ---------------------------------------------------------------- East
  { id: "kolkata", name: "Kolkata", region: "East", tier: "metro", index: 0.77, measured: true,
    remoteAccessPerYear: 0, schooling: "international", tertiaryCare: "In the city",
    note: "23% cheaper than Bengaluru, with housing 37% cheaper. The cheapest true metro." },
  { id: "bhubaneswar", name: "Bhubaneswar", region: "East", tier: "tier2", index: 0.60, measured: false,
    remoteAccessPerYear: 20_000, schooling: "national", tertiaryCare: "AIIMS Bhubaneswar",
    note: "Cheap, planned, and has an AIIMS. Thin on international schooling." },

  // ---------------------------------------------------------------- Hills
  { id: "dehradun", name: "Dehradun", region: "Hill stations", tier: "tier2", index: 0.62, measured: false,
    remoteAccessPerYear: 35_000, schooling: "boarding-hub", tertiaryCare: "Max and AIIMS Rishikesh, ~30 km",
    note: "The hill option that is still a real city: an airport, AIIMS Rishikesh half an hour away, and the country's densest cluster of boarding schools." },
  { id: "mussoorie", name: "Mussoorie", region: "Hill stations", tier: "hill-station", index: 0.70, measured: false,
    remoteAccessPerYear: 70_000, schooling: "boarding-hub", tertiaryCare: "Dehradun, ~35 km down the hill",
    note: "Woodstock and the old hill-station schools are here. Everything else comes up from Dehradun, and so does the price." },
  { id: "shimla", name: "Shimla", region: "Hill stations", tier: "hill-station", index: 0.68, measured: false,
    remoteAccessPerYear: 80_000, schooling: "boarding-hub", tertiaryCare: "IGMC locally; complex cases to Chandigarh, ~115 km",
    note: "A state capital, so better serviced than most hill towns. Winter access and the drive to Chandigarh are the constraints." },
  { id: "madikeri", name: "Madikeri (Coorg)", region: "Hill stations", tier: "hill-station", index: 0.60, measured: false,
    remoteAccessPerYear: 75_000, schooling: "limited", tertiaryCare: "Local multi-specialty; super-specialty in Mysuru, ~115 km",
    note: "Living costs run around ₹25,000 a month. A district hospital and a private multi-specialty are in town, but anything serious is a three-hour drive to Mysuru and five to Bengaluru." },
  { id: "ooty", name: "Ooty / Coonoor", region: "Hill stations", tier: "hill-station", index: 0.62, measured: false,
    remoteAccessPerYear: 70_000, schooling: "boarding-hub", tertiaryCare: "Coimbatore, ~85 km down the ghat",
    note: "Coimbatore's hospitals are close enough to be a real backstop, which is not true of most hill stations." },
  { id: "kodaikanal", name: "Kodaikanal", region: "Hill stations", tier: "hill-station", index: 0.58, measured: false,
    remoteAccessPerYear: 85_000, schooling: "boarding-hub", tertiaryCare: "Madurai, ~120 km",
    note: "Cheap and beautiful. Two to three hours of ghat road to the nearest serious hospital." },
  { id: "kasauli", name: "Kasauli / Solan", region: "Hill stations", tier: "hill-station", index: 0.65, measured: false,
    remoteAccessPerYear: 55_000, schooling: "boarding-hub", tertiaryCare: "PGIMER Chandigarh, ~60 km",
    note: "The best-placed northern hill town for healthcare — an hour and a half to PGIMER." },
  { id: "manali", name: "Manali", region: "Hill stations", tier: "hill-station", index: 0.65, measured: false,
    remoteAccessPerYear: 110_000, schooling: "limited", tertiaryCare: "Chandigarh, ~300 km",
    note: "The most remote option here. Winter road closures make the access figure a floor, not an estimate." },
  { id: "rishikesh", name: "Rishikesh / Haridwar", region: "Hill stations", tier: "small-town", index: 0.58, measured: false,
    remoteAccessPerYear: 30_000, schooling: "limited", tertiaryCare: "AIIMS Rishikesh, in town",
    note: "An AIIMS in a small town at foothill prices — unusually good access for the cost." },
  { id: "almora", name: "Almora / Ranikhet", region: "Hill stations", tier: "hill-station", index: 0.52, measured: false,
    remoteAccessPerYear: 100_000, schooling: "limited", tertiaryCare: "Haldwani, ~90 km; serious cases to Delhi",
    note: "The cheapest entry here, and the thinnest on services. Suits a couple in good health far better than a household with dependents." },
  { id: "panchgani", name: "Panchgani / Mahabaleshwar", region: "Hill stations", tier: "hill-station", index: 0.62, measured: false,
    remoteAccessPerYear: 60_000, schooling: "boarding-hub", tertiaryCare: "Pune, ~100 km",
    note: "Close enough to Pune that a specialist appointment is a day trip, not an expedition." },
];

export const REGIONS: Region[] = ["North", "West", "South", "East", "Hill stations"];

export function cityById(id: string): CityProfile {
  return CITIES.find((c) => c.id === id) ?? CITIES.find((c) => c.id === "bengaluru")!;
}

/** Typical all-in day-school fee per child per year, by what the town offers. */
export const SCHOOL_PRESETS: Record<SchoolingAccess, { label: string; perYear: number }[]> = {
  international: [
    { label: "Premium international (IB)", perYear: 9 * LAKH },
    { label: "Mid international", perYear: 5 * LAKH },
    { label: "Good CBSE / ICSE", perYear: 2.5 * LAKH },
  ],
  national: [
    { label: "Top local CBSE / ICSE", perYear: 3 * LAKH },
    { label: "Good CBSE / ICSE", perYear: 1.5 * LAKH },
    { label: "Residential school elsewhere", perYear: 15 * LAKH },
  ],
  "boarding-hub": [
    { label: "Residential school (Doon, Woodstock tier)", perYear: 18 * LAKH },
    { label: "Mid residential school", perYear: 10 * LAKH },
    { label: "Local day school", perYear: 1.5 * LAKH },
  ],
  limited: [
    { label: "Local day school", perYear: 1.2 * LAKH },
    { label: "Residential school elsewhere", perYear: 15 * LAKH },
  ],
};

export const SCHOOLING_WARNING: Record<SchoolingAccess, string | null> = {
  international: null,
  national: "No international-curriculum day school locally. Fine for most families; worth checking if you expect the children to apply abroad.",
  "boarding-hub": "Known for residential schools rather than day schools. Budget for boarding, or accept a local day school.",
  limited: "Local-board schooling only. With school-age children this usually means boarding elsewhere — which is the single largest cost this town adds back.",
};

/**
 * How the household scales the remote-access cost. Two adults are the base;
 * dependents add to it, and elderly dependents add most, because they are the
 * ones making the specialist trips.
 */
export function accessMultiplier(childCount: number, parentCount: number): number {
  return 1 + 0.25 * childCount + 0.6 * parentCount;
}
